import { getUnavailableVehicleDataV2 } from "api/vehicles/vehicle";
import { useData } from "data/data-provider";
import moment from "moment";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { changeDate } from "utils/changeDate";
import ClearDataButton from "../ClearDataButton";
import DatePickerCustomDay from "../DatePickerCustomDay";

// Types
import {
  DatePickerType,
  RentalCarDatesRangeInputProps,
  TabType,
  UnavailabilityData,
} from "./types/rentalCarDatesRangeInput.types";

// Utils
import {
  createDateWithoutTimezone,
  formatDate,
  parsePartialUnavailability,
} from "./utils/rentalCarDatesRangeInput.utils";
import {
  getAllWeeksForSelection,
  getSelectedWeeksDateRange,
  weekContainsUnavailableDates,
} from "./utils/weeksHelper";

import { useDialInteraction } from "./hooks/useDialInteraction";

// Hooks
import { useClickOutside } from "./hooks/useClickOutside";
import { useDateAvailability } from "./hooks/useDateAvailability";
import { useScreenSize } from "./hooks/useScreenSize";

// Components
import DatesView from "./components/DatesView";
import TabSwitcher from "./components/TabSwitcher";
import WeeksView from "./components/WeeksView";
import MonthsView from "./components/MonthsView";
import { CalendarIcon, XMarkIcon } from "@heroicons/react/24/outline";

const DateRangeSelection: FC<RentalCarDatesRangeInputProps> = ({
  className = "",
  fieldClassName = "[ nc-hero-field-padding ]",
  selectedCar,
  isMiddle,
  searchBar,
  minDays,
  maxDays,
  bookingDetails,
  onSaveDates,
}) => {
  const {
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    setPartiallyAvailableStartDate,
    setPartiallyAvailableEndDate,
    setPartiallyUnavailableTimes,
    partiallyUnavailableTimes,
    setLongTermRentalDuration,
    setLongTermRentalNature,
    pickupLocation,
    startTime,
    endTime,
    setStartTime,
    setEndTime,
    s_Time,
    e_Time,
    activeTab,
    setSelectedWeeks,
    selectedWeeks,
    monthEndDate,
    setMonthEndDate,
    monthStartDate,
    setMonthStartDate,
    selectedMonth,
    setActiveTab,
    setSelectedMonth,
    weeksViewMonth,
  } = useData();

  const dialRef = useRef<HTMLDivElement>(null);

  const [fullyUnavailableDates, setFullyUnavailableDates] = useState<Date[]>(
    [],
  );
  const [partialUnavailability, setPartialUnavailability] = useState<{
    [key: string]: { from: string; to: string }[];
  }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [calendarViewDate, setCalendarViewDate] = useState<Date | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const { isDragging, handleMouseDown, handleTouchStart } = useDialInteraction(
    dialRef,
    setSelectedMonth,
  );

  const [showDatePicker, setShowDatePicker] = useState<DatePickerType>(null);
  const [tempSelectedDate, setTempSelectedDate] = useState<Date | null>(null);
  const [tempStartDate, setTempStartDate] = useState<Date | null>(null);
  const [tempEndDate, setTempEndDate] = useState<Date | null>(null);

  const { isLargeScreen } = useScreenSize();
  const monthsToShow = isLargeScreen ? 2 : 1;

  const { isDateSelectable, isRangeAvailable, isPartialOverlapValid } =
    useDateAvailability(fullyUnavailableDates, partialUnavailability);

  useClickOutside(panelRef, isPanelOpen && !showDatePicker, () => setIsPanelOpen(false));

  // Initialize temp dates when panel opens
  useEffect(() => {
    if (isPanelOpen) {
      setTempStartDate(startDate);
      setTempEndDate(endDate);
    }
  }, [isPanelOpen]);

  // Update end date when selected month changes
  useEffect(() => {
    const newEndDate = moment(monthStartDate)
      .add(selectedMonth * 30 - 1, "days")
      .toDate();
    setMonthEndDate(newEndDate);
  }, [selectedMonth, monthStartDate]);

  // Calculate months from date range
  const calculateMonthsFromDates = useCallback((start: Date, end: Date) => {
    const days = moment(end).diff(moment(start), "days") + 1;
    const months = Math.round(days / 30);
    return Math.max(1, Math.min(12, months));
  }, []);

  // Open date picker modal
  const openDatePickerModal = (type: DatePickerType) => {
    setShowDatePicker(type);
    setTempSelectedDate(type === "start" ? monthStartDate : monthEndDate);
  };

  const handleModalDateChange = (date: Date | null) => {
    if (date) setTempSelectedDate(date);
  };

  // Save the selected date from modal
  const handleModalSave = () => {
    if (!tempSelectedDate || !showDatePicker) return;

    if (showDatePicker === "start") {
      setMonthStartDate(tempSelectedDate);
      const months = calculateMonthsFromDates(tempSelectedDate, monthEndDate);
      setSelectedMonth(months);
    } else if (showDatePicker === "end") {
      setMonthEndDate(tempSelectedDate);
      const months = calculateMonthsFromDates(monthStartDate, tempSelectedDate);
      setSelectedMonth(months);
    }
    setShowDatePicker(null);
    setTempSelectedDate(null);
  };

  // Close modal without saving
  const handleModalClose = () => {
    setShowDatePicker(null);
    setTempSelectedDate(null);
  };

  const fetchUnavailableDates = async (startDate: Date, endDate: Date) => {
    if (!selectedCar?.id) {
      console.warn("No car selected, unable to fetch unavailable dates.");
      return;
    }

    setIsLoading(true);
    try {
      const startDateString = new Date(startDate);
      startDateString.setMonth(startDateString.getMonth() - 1);
      const endDateString = new Date(endDate);
      endDateString.setMonth(endDateString.getMonth() + 1);

      const startDateStr = formatDate(startDateString);
      const endDateStr = formatDate(endDateString);

      const response = await getUnavailableVehicleDataV2(
        {
          start_date: startDateStr,
          end_date: endDateStr,
          booking_to_exclude: bookingDetails?.id,
        },
        selectedCar.id,
        pickupLocation?.id,
      );

      if (response.data) {
        const unavailableData = response.data as UnavailabilityData;
        const fullyUnavailable = unavailableData.full_unavailable.map(
          (dateStr) => createDateWithoutTimezone(dateStr),
        );
        setFullyUnavailableDates(fullyUnavailable);
        const partialUnavailable = parsePartialUnavailability(
          unavailableData.partial_unavailable,
        );
        setPartialUnavailability(partialUnavailable);
        if (setPartiallyUnavailableTimes) {
          setPartiallyUnavailableTimes(partialUnavailable as any);
        }
      }
    } catch (error) {
      console.error("Error fetching unavailable dates:", error);
      toast.error("Failed to load availability data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const onChangeDate = (dates: [Date | null, Date | null]) => {
    const startMoment = moment(dates[0]);
    const endMoment = moment(dates[1]);
    const isCheck = isRangeAvailable(startMoment, endMoment);
    // Use default time if time is empty
    const validStartTime =
      startTime && startTime.trim() !== "" ? startTime : s_Time;
    const validEndTime = endTime && endTime.trim() !== "" ? endTime : e_Time;

    if (!isCheck) {
      setTempStartDate(null);
      setTempEndDate(null);
      toast.error("Selected range is fully unavailable.");
      return;
    }

    const isPartialValid = isPartialOverlapValid(startMoment, endMoment);
    if (!isPartialValid) {
      setTempStartDate(null);
      setTempEndDate(null);
      toast.error(
        "Selected range contains a partially unavailable date in the middle.",
      );
      return;
    }
    // Update temporary state only
    setTempStartDate(dates[0]);
    setTempEndDate(dates[1]);

    const today = new Date();
    const visibleEndDate = new Date(today);
    visibleEndDate.setMonth(today.getMonth() + monthsToShow);
    fetchUnavailableDates(today, visibleEndDate);
  };

  useEffect(() => {
    const today = new Date();
    const threeMonthsLater = new Date();
    threeMonthsLater.setMonth(today.getMonth() + monthsToShow);
    fetchUnavailableDates(today, threeMonthsLater);
  }, [selectedCar?.id, monthsToShow, pickupLocation]);

  // Fetch unavailable dates when weeks view month changes
  useEffect(() => {
    if (activeTab === 'weeks' && weeksViewMonth) {
      const monthStart = moment(weeksViewMonth).startOf('month').toDate();
      const monthEnd = moment(weeksViewMonth).endOf('month').toDate();
      fetchUnavailableDates(monthStart, monthEnd);
    }
  }, [weeksViewMonth, activeTab]);

  useEffect(() => {
    if (activeTab === 'weeks' && selectedWeeks && selectedWeeks.length > 0 && startDate && endDate) {
      const currentStartTime = startTime && startTime.trim() !== "" ? startTime : s_Time;
      const currentEndTime = endTime && endTime.trim() !== "" ? endTime : e_Time;
      
      if (currentStartTime && currentEndTime) {
        const allWeeks = getAllWeeksForSelection(selectedWeeks, fullyUnavailableDates);
        const range = getSelectedWeeksDateRange(selectedWeeks, allWeeks, currentStartTime, currentEndTime);
        if (range) {
          setStartDate(range.start);
          setEndDate(range.end);
        }
      }
    }
  }, [startTime, endTime]); 

  useEffect(() => {
    if (!startDate || !endDate || Object.keys(partialUnavailability).length === 0) return;
    
    const currentStartTime = startTime && startTime.trim() !== "" ? startTime : s_Time;
    const currentEndTime = endTime && endTime.trim() !== "" ? endTime : e_Time;

    const startTimeBlocked = isTimeBlocked(startDate, currentStartTime);
    const endTimeBlocked = isTimeBlocked(endDate, currentEndTime);
    
    if (startTimeBlocked || endTimeBlocked) {
      const opStartDefault = pickupLocation?.operational_start_time ?? s_Time;
      const opEndDefault = pickupLocation?.operational_end_time ?? e_Time;
      const validStartTime = startTimeBlocked ? getAvailableTime(startDate, opStartDefault) : currentStartTime;
      const validEndTime = endTimeBlocked ? getAvailableTime(endDate, opEndDefault) : currentEndTime;

      if (validStartTime === null || validEndTime === null) {
        const opStart = pickupLocation?.operational_start_time;
        const opEnd = pickupLocation?.operational_end_time;
        toast.error(
          opStart && opEnd
            ? `No available time slot on the selected date. Operational hours: ${moment(opStart, "HH:mm").format("h:mm A")} – ${moment(opEnd, "HH:mm").format("h:mm A")}.`
            : "No available time slot on the selected date."
        );
        setStartDate(null);
        setEndDate(null);
        return;
      }

      // Update the date objects with the new times
      if (validStartTime !== currentStartTime || validEndTime !== currentEndTime) {
        changeDate([startDate, endDate], setStartDate, setEndDate, validStartTime, validEndTime);
        // Also update time state
        if (validStartTime !== startTime) {
          setStartTime(validStartTime);
        }
        if (validEndTime !== endTime) {
          setEndTime(validEndTime);
        }
      }
    }
  }, [partialUnavailability, startDate, endDate]);

  // Helper function to check if a time is blocked
  const isTimeBlocked = (
    date: Date | null | undefined,
    time: string,
  ): boolean => {
    if (!date) return false;
    // Use local partialUnavailability state for immediate accuracy
    const unavailabilityData = partialUnavailability || partiallyUnavailableTimes || {};
    const dateKey = moment(date).format("YYYY-MM-DD");
    const ranges = unavailabilityData[dateKey];
    if (!Array.isArray(ranges) || ranges.length === 0) return false;

    const timeMoment = moment(time, "HH:mm");
    return ranges.some(({ from, to }: { from: string; to: string }) => {
      const fromMoment = moment(from, "HH:mm:ss");
      const toMoment = moment(to, "HH:mm:ss");
      return (
        timeMoment.isSameOrAfter(fromMoment) &&
        timeMoment.isSameOrBefore(toMoment)
      );
    });
  };

  // Helper function to find next available time if default is unavailable.
  // Returns null when every slot within operational hours is also booking-blocked.
  const getAvailableTime = (
    date: Date | null | undefined,
    defaultTime: string,
  ): string | null => {
    if (!date) return defaultTime;

    const opStart = pickupLocation?.operational_start_time ?? null;
    const opEnd = pickupLocation?.operational_end_time ?? null;

    const isOutsideOp = (t: string): boolean => {
      if (!opStart || !opEnd) return false;
      const tm = moment(t, "HH:mm");
      return tm.isBefore(moment(opStart, "HH:mm")) || tm.isAfter(moment(opEnd, "HH:mm"));
    };

    // If default time is available and within operational hours, use it
    if (!isTimeBlocked(date, defaultTime) && !isOutsideOp(defaultTime)) {
      return defaultTime;
    }

    // Find first available time slot within operational hours
    const slots = Array.from({ length: 24 }, (_, h) => [
      `${h.toString().padStart(2, "0")}:00`,
      `${h.toString().padStart(2, "0")}:30`,
    ]).flat();

    for (const time of slots) {
      if (!isTimeBlocked(date, time) && !isOutsideOp(time)) {
        return time;
      }
    }

    // No slot within operational hours is available
    return null;
  };

  const handleMonthChange = (date: Date) => {
    setCalendarViewDate(date);
    const viewStartDate = new Date(date.getFullYear(), date.getMonth(), 1);
    const viewEndDate = new Date(
      date.getFullYear(),
      date.getMonth() + monthsToShow,
      0,
    );
    fetchUnavailableDates(viewStartDate, viewEndDate);
  };

  const renderDayContents = (day: number, date?: Date) => {
    if (!date) return day;
    const dateStr = formatDate(date);
    const isPartial = !!partialUnavailability[dateStr];

    return (
      <DatePickerCustomDay
        dayOfMonth={day}
        date={date}
        unavailableDates={fullyUnavailableDates}
        partiallyUnavailable={isPartial}
        partialTimes={partialUnavailability[dateStr]}
      />
    );
  };

  useEffect(() => {
    if (isPanelOpen && window.innerWidth < 768) {
      setTimeout(() => {
        if (panelRef.current) {
          const topPosition = panelRef.current.getBoundingClientRect().top;
          const offset =
            window.pageYOffset || document.documentElement.scrollTop;
          const targetScrollPosition = topPosition + offset - 120;

          window.scrollTo({
            top: targetScrollPosition,
            behavior: "smooth",
          });
        }
      }, 100);
    }
  }, [isPanelOpen]);

  // Render active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case "dates":
        return (
          <DatesView
            startDate={tempStartDate}
            endDate={tempEndDate}
            onChangeDate={onChangeDate}
            monthsToShow={monthsToShow}
            calendarViewDate={calendarViewDate}
            isDateSelectable={isDateSelectable}
            renderDayContents={renderDayContents}
            handleMonthChange={handleMonthChange}
          />
        );
      case "weeks":
        return (
          <WeeksView
            unavailableDates={fullyUnavailableDates}
            partialUnavailability={partialUnavailability}
          />
        );
      case "months":
        return (
          <MonthsView
            dialRef={dialRef}
            isDragging={isDragging}
            handleMouseDown={handleMouseDown}
            handleTouchStart={handleTouchStart}
            openDatePickerModal={openDatePickerModal}
            showDatePicker={showDatePicker}
            tempSelectedDate={tempSelectedDate}
            handleModalDateChange={handleModalDateChange}
            handleModalSave={handleModalSave}
            handleModalClose={handleModalClose}
            monthsToShow={monthsToShow}
            calendarViewDate={calendarViewDate}
            startDate={startDate}
            isDateSelectable={isDateSelectable}
            renderDayContents={renderDayContents}
            handleMonthChange={handleMonthChange}
            isLoading={isLoading}
          />
        );
      default:
        return (
          <DatesView
            startDate={tempStartDate}
            endDate={tempEndDate}
            onChangeDate={onChangeDate}
            monthsToShow={monthsToShow}
            calendarViewDate={calendarViewDate}
            isDateSelectable={isDateSelectable}
            renderDayContents={renderDayContents}
            handleMonthChange={handleMonthChange}
          />
        );
    }
  };

  // Handle save for months
  const handleMonthSave = () => {
    const startMoment = moment(monthStartDate);
    const endMoment = moment(monthEndDate);

    if (isRangeAvailable(startMoment, endMoment)) {
      // Check if this is first selection (no dates were previously selected)
      const isFirstSelection = !startDate && !endDate;
      const defaultStartTime = startTime && startTime.trim() !== "" ? startTime : s_Time;
      const defaultEndTime = endTime && endTime.trim() !== "" ? endTime : e_Time;
      
      // On first selection, always validate. On subsequent selections, only adjust if blocked
      const shouldCheckStartTime = isFirstSelection || isTimeBlocked(monthStartDate, defaultStartTime);
      const shouldCheckEndTime = isFirstSelection || isTimeBlocked(monthEndDate, defaultEndTime);
      const opStartDefault = pickupLocation?.operational_start_time ?? s_Time;
      const opEndDefault = pickupLocation?.operational_end_time ?? e_Time;
      const validStartTime = (monthStartDate && shouldCheckStartTime)
        ? getAvailableTime(monthStartDate, opStartDefault)
        : defaultStartTime;
      const validEndTime = (monthEndDate && shouldCheckEndTime)
        ? getAvailableTime(monthEndDate, opEndDefault)
        : defaultEndTime;

      if (validStartTime === null || validEndTime === null) {
        const opStart = pickupLocation?.operational_start_time;
        const opEnd = pickupLocation?.operational_end_time;
        toast.error(
          opStart && opEnd
            ? `No available time slot on the selected date. Operational hours: ${moment(opStart, "HH:mm").format("h:mm A")} – ${moment(opEnd, "HH:mm").format("h:mm A")}.`
            : "No available time slot on the selected date."
        );
        setStartDate(null);
        setEndDate(null);
        return;
      }

      changeDate(
        [monthStartDate, monthEndDate],
        setStartDate,
        setEndDate,
        validStartTime,
        validEndTime,
      );
      // 👇 Calculate total months (inclusive):
      const totalDays = endMoment.diff(startMoment, "days") + 1;
      const totalMonths = Math.round(totalDays / 30);
      setLongTermRentalDuration(totalMonths);
    } else {
      toast.error("Selected period contains unavailable dates.");
      setStartDate(null);
      setEndDate(null);
      setSelectedMonth(1);
      setSelectedWeeks([]);
    }
  };

  const handleWeeksSave = () => {
    const allWeeks = getAllWeeksForSelection(selectedWeeks, fullyUnavailableDates);
    const range = getSelectedWeeksDateRange(selectedWeeks, allWeeks, startTime, endTime);
    if (!range) {
      toast.error("Please select at least one week.");
      return;
    }

    // Check if any selected week contains unavailable dates
    const sortedWeeks = [...selectedWeeks].sort();
    const firstWeekKey = sortedWeeks[0];

    const weeksWithUnavailability = allWeeks.filter((week) => {
      const keyDate = week.isCurrentWeek ? week.effectiveStart : week.start;
      const key = moment(keyDate).format("YYYY-MM-DD");
      const isFirstWeek = key === firstWeekKey;

      return (
        selectedWeeks.includes(key) &&
        weekContainsUnavailableDates(
          week,
          fullyUnavailableDates,
          partialUnavailability,
          isFirstWeek, // Allow partial availability only for first week
        )
      );
    });

    if (weeksWithUnavailability.length > 0) {
      toast.error("One or more selected weeks contain unavailable dates.");
      return;
    }

    const startMoment = moment(range.start);
    const endMoment = moment(range.end);

    if (isRangeAvailable(startMoment, endMoment)) {
      // Check if this is first selection (no dates were previously selected)
      const isFirstSelection = !startDate && !endDate;
      const defaultStartTime = startTime && startTime.trim() !== "" ? startTime : s_Time;
      const defaultEndTime = endTime && endTime.trim() !== "" ? endTime : e_Time;
      
      // On first selection, always validate. On subsequent selections, only adjust if blocked
      const shouldCheckStartTime = isFirstSelection || isTimeBlocked(range.start, defaultStartTime);
      const shouldCheckEndTime = isFirstSelection || isTimeBlocked(range.end, defaultEndTime);
      const opStartDefault = pickupLocation?.operational_start_time ?? s_Time;
      const opEndDefault = pickupLocation?.operational_end_time ?? e_Time;
      const validStartTime = (range.start && shouldCheckStartTime)
        ? getAvailableTime(range.start, opStartDefault)
        : defaultStartTime;
      const validEndTime = (range.end && shouldCheckEndTime)
        ? getAvailableTime(range.end, opEndDefault)
        : defaultEndTime;

      if (validStartTime === null || validEndTime === null) {
        const opStart = pickupLocation?.operational_start_time;
        const opEnd = pickupLocation?.operational_end_time;
        toast.error(
          opStart && opEnd
            ? `No available time slot on the selected date. Operational hours: ${moment(opStart, "HH:mm").format("h:mm A")} – ${moment(opEnd, "HH:mm").format("h:mm A")}.`
            : "No available time slot on the selected date."
        );
        setStartDate(null);
        setEndDate(null);
        return;
      }

      changeDate(
        [range.start, range.end],
        setStartDate,
        setEndDate,
        validStartTime,
        validEndTime,
      );
      setLongTermRentalDuration(selectedWeeks.length);
    } else {
      toast.error("Selected weeks contain unavailable dates.");
    }
  };

  const handleSave = () => {
    // Get default times and check if they're available
    const defaultStartTime = startTime && startTime.trim() !== "" ? startTime : s_Time;
    const defaultEndTime = endTime && endTime.trim() !== "" ? endTime : e_Time;

    switch (activeTab) {
      case "weeks":
        handleWeeksSave();
        setLongTermRentalNature("weekly");
        setSelectedMonth(1);
        setMonthStartDate(new Date());
        setMonthEndDate(moment().add(29, "days").toDate());
        break;
      case "months":
        handleMonthSave();
        setLongTermRentalNature("monthly");
        setSelectedWeeks([]);
        break;
      case "dates":
      default:
        if (tempStartDate && tempEndDate) {
          // Check if this is first selection (no dates were previously selected)
          const isFirstSelection = !startDate && !endDate;
          const defaultStartTime = startTime && startTime.trim() !== "" ? startTime : s_Time;
          const defaultEndTime = endTime && endTime.trim() !== "" ? endTime : e_Time;
          
          // On first selection, always validate. On subsequent selections, only adjust if blocked
          const shouldCheckStartTime = isFirstSelection || isTimeBlocked(tempStartDate, defaultStartTime);
          const shouldCheckEndTime = isFirstSelection || isTimeBlocked(tempEndDate, defaultEndTime);
          const opStartDefault = pickupLocation?.operational_start_time ?? s_Time;
          const opEndDefault = pickupLocation?.operational_end_time ?? e_Time;
          const validStartTime = (tempStartDate && shouldCheckStartTime)
            ? getAvailableTime(tempStartDate, opStartDefault)
            : defaultStartTime;
          const validEndTime = (tempEndDate && shouldCheckEndTime)
            ? getAvailableTime(tempEndDate, opEndDefault)
            : defaultEndTime;

          if (validStartTime === null || validEndTime === null) {
            const opStart = pickupLocation?.operational_start_time;
            const opEnd = pickupLocation?.operational_end_time;
            toast.error(
              opStart && opEnd
                ? `No available time slot on the selected date. Operational hours: ${moment(opStart, "HH:mm").format("h:mm A")} – ${moment(opEnd, "HH:mm").format("h:mm A")}.`
                : "No available time slot on the selected date."
            );
            setTempStartDate(null);
            setTempEndDate(null);
            return;
          }

          changeDate(
            [tempStartDate, tempEndDate],
            setStartDate,
            setEndDate,
            validStartTime,
            validEndTime,
          );
          const startMoment = moment(tempStartDate);
          const endMoment = moment(tempEndDate);
          const totalDays = endMoment.diff(startMoment, "days") + 1;
          if (totalDays) {
            setLongTermRentalDuration(totalDays);
          }
        }
        setLongTermRentalNature("daily");
        setSelectedWeeks([]);
        setSelectedMonth(1);
        setMonthStartDate(new Date());
        setMonthEndDate(moment().add(29, "days").toDate());
        break;
    }
    setIsPanelOpen(false);

    // Trigger invoice update if callback is provided
    if (onSaveDates) {
      onSaveDates();
    }
  };

  return (
    <>
      <div className={`RentalCarDatesRangeInput relative flex ${className}`}>
        <div className={`flex-1 flex items-center focus:outline-none`}>
          {/* Main container with grid layout */}
          <div
            className={`flex-1 flex relative ${fieldClassName} items-center space-x-3 focus:outline-none cursor-pointer`}
            onClick={() => setIsPanelOpen((open) => !open)}
          >
            <div className="text-text-color ">
              <CalendarIcon className="w-5 h-5 lg:w-7 lg:h-7" />
            </div>
            <div className="flex-grow text-left">
              <span
                className={`block xl:text-lg font-semibold ${"text-text-color"}`}
              >
                {startDate?.toLocaleDateString("en-US", {
                  month: "short",
                  day: "2-digit",
                }) || "Add dates"}
                {endDate
                  ? " - " +
                    endDate?.toLocaleDateString("en-US", {
                      month: "short",
                      day: "2-digit",
                    })
                  : ""}
              </span>
              <span
                className={`block mt-1 text-sm ${"text-text-color"} leading-none font-light`}
              >
                {"Pickup & Drop off Date"}
              </span>
            </div>

            {startDate && isPanelOpen && (
              <ClearDataButton
                onClick={() => {
                  setStartDate(null);
                  setEndDate(null);
                  setTempStartDate(null);
                  setTempEndDate(null);
                  setPartiallyAvailableStartDate(null);
                  setPartiallyAvailableEndDate(null);
                  setSelectedWeeks([]);
                  setSelectedMonth(0);
                  setActiveTab("dates");
                  setMonthStartDate(new Date());
                  setMonthEndDate(moment().add(29, "days").toDate());
                }}
              />
            )}
          </div>
        </div>

        {isPanelOpen && (
          <div className="h-8 absolute self-center top-1/2 -translate-y-1/2 z-0 -left-0.5 right-1"></div>
        )}

        {isPanelOpen && (
          <div
            ref={panelRef}
            className="absolute z-50 left-0 top-full w-full mt-2 rounded-2xl shadow-lg ring-1 ring-black/5 bg-white"
            tabIndex={-1}
            style={{ minWidth: 320 }}
            onScroll={(e) => e.stopPropagation()}
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            <div className="overflow-hidden rounded-2xl shadow-lg ring-1 ring-black/5 bg-white p-8">
              {<TabSwitcher />}

              {isLoading ? (
                <div className="pointer-events-none opacity-60 select-none">
                  <div className="relative">{renderTabContent()}</div>
                  <div className="absolute left-0 bottom-0 w-full px-8">
                    <div className="h-1 bg-gray-100 rounded-full overflow-hidden relative">
                      <div className="bar-loader h-full bg-blue-400 rounded-full absolute"></div>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-white opacity-0 pointer-events-auto" />
                </div>
              ) : (
                renderTabContent()
              )}

              <div className="flex justify-between items-center align-center mt-6">
                <div>
                  {selectedWeeks.length > 0 && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        setSelectedWeeks([]);
                      }}
                      className=" w-[120px] text-red-600 hover:text-red-700 hover:bg-red-100 bg-red-50 font-semibold rounded-lg py-3 transition-all space-x-2"
                    >
                      <span>Clear Selection</span>
                    </button>
                  )}
                </div>
                <div>
                  <button
                    className=" w-[120px] bg-primary-200 hover:bg-primary-700 text-button-color font-semibold rounded-lg py-3 transition-all space-x-2"
                    onClick={(e) => {
                      e.preventDefault();
                      handleSave();
                    }}
                  >
                    <span>Save Dates</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default DateRangeSelection;
